import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { EventType, KeyType } from 'constants/enums'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { setStyles } from 'utils/dom'
import { getStyles } from 'utils/dom'
import { hasTouch, isMobile } from 'utils/system/device'
import { Menu } from './menu'

const {
    header: { menu: menuClass, mainMenu: mainMenuClass }
} = CLASS_NAME

/**
 * Arrow key to index for mapping key action to direction
 * ⬆️ is 0, and clockwise matching with CSS margin/padding property
 */
export enum ArrowDirection {
    ArrowUp = 0,
    Up = 0,
    ArrowRight = 1,
    Right = 1,
    ArrowDown = 2,
    Down = 2,
    // eslint-disable-next-line no-magic-numbers
    ArrowLeft = 3,
    // eslint-disable-next-line no-magic-numbers
    Left = 3
}

/**
 * Menu Item
 * the root element is li.header-menu-nav__item
 */
export class MenuItem extends ComponentBase {
    /**
     * childrenWrapper: <ul> element in the component
     * children: <li> elements of it
     */
    public childrenWrapper?: Nullable<HTMLUListElement>
    private children?: HTMLLIElement[]
    private childrenWrapperArrow?: Nullable<HTMLSpanElement>
    /**
     * Linked list for root (L1) items
     * Used for resetting styles of L1 items
     */
    private _nextRootItem?: MenuItem
    /**
     * Linked list for prev/ next items
     * It starts from the first L1 to the last menu item
     * Used for Tab and Shift + Tab action
     */
    private _prevElement?: MenuItem
    private _nextElement?: MenuItem
    /**
     * Linked list for keyboard action
     * It matches with `ArrowDirection` values (⬆️ is 0)
     */
    private _arrowElement: { [key: number]: MenuItem } = {}
    /**
     * <a> or <button> element in the component
     */
    private focusItem: HTMLAnchorElement | HTMLButtonElement

    /**
     * Initialize elements and set event listeners
     * @param {HTMLLIElement} element li.header-menu-nav__item element of the menu item
     * @param {number} level The depth of the menu
     */
    public constructor(
        private readonly element: HTMLLIElement,
        private readonly level: number,
        private readonly targetMenu: Menu
    ) {
        super()

        if (!this.initElements()) {
            return
        }
        this.initEventListeners()
    }

    /**
     * Initialize elements. <a> is mandatory
     */
    private initElements(): boolean {
        const focusItem = this.getElement<
            HTMLAnchorElement | HTMLButtonElement
        >(`.${menuClass.focusItem}`, this.element)
        if (!focusItem) {
            return false
        }
        this.focusItem = focusItem

        if (this.level === 1) {
            this.childrenWrapper = this.getElement<HTMLUListElement>(
                'ul',
                this.element
            )
            // if no child UL don't look for data-level 2 li
            // or you get just all of them from the page
            if (this.childrenWrapper) {
                this.children = this.getElements<HTMLLIElement>(
                    `.${menuClass.item}[${ATTR_NAME.header.menu.level}="2"]`,
                    this.childrenWrapper
                )
                this.childrenWrapperArrow = this.getElement<HTMLSpanElement>(
                    `.${mainMenuClass.arrow}`,
                    this.element
                )
            }
        }
        return true
    }

    /**
     * Event listeners
     * Keyboard actions and mouse over
     */
    private initEventListeners(): void {
        // Keyboard actions
        this.focusItem.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) =>
            this.handleKeyEvents(e)
        )

        // Mouse hover should steal the focus
        this.focusItem.addEventListener(
            EventType.MOUSE_OVER,
            (e: MouseEvent) => {
                e.preventDefault()
                this.focus()
            }
        )

        // Mobile: touch opens the sub-menus if exists
        // If Arrow visible, droppable mnu is handled
        if (
            this.level === 1 &&
            this.children &&
            this.children.length &&
            this.childrenWrapperArrow &&
            hasTouch() &&
            isMobile()
        ) {
            this.childrenWrapperArrow.removeAttribute('aria-hidden')
            this.childrenWrapperArrow.setAttribute(
                'aria-label',
                'Click to open sub menu'
            )
            this.childrenWrapperArrow.setAttribute('role', 'button')
            this.childrenWrapperArrow.setAttribute('tabindex', '0')
            this.childrenWrapperArrow.addEventListener(
                EventType.TOUCH_START,
                (e: TouchEvent) => {
                    this.handleTouchDroppableMobile(e)
                }
            )
            this.childrenWrapperArrow.addEventListener(
                EventType.KEYDOWN,
                (e: KeyboardEvent) => {
                    if (e.key == KeyType.ENTER) {
                        this.handleTouchDroppableMobile(e)
                    }
                }
            )
        }

        // Tablet: first click opens the sub-menus if exists
        if (
            this.level === 1 &&
            this.children &&
            this.children.length &&
            hasTouch() &&
            !isMobile()
        ) {
            this.focusItem.addEventListener(
                EventType.TOUCH_START,
                (e: TouchEvent) => {
                    this.handleTouch(e)
                }
            )
        }

        // clicks for navigating should check if it was react app route
        // if it was, close the menu
        if (this.focusItem.hasAttribute('href')) {
            // typecast, hasAttribute already checked, cannot be null
            const link = this.focusItem.getAttribute('href') as string

            this.focusItem.addEventListener(EventType.CLICK, () => {
                this.handleRouting(link)
            })
        }
    }

    /**
     * Element setters: prev
     * @param {MenuItem} element
     */
    public set prevElement(element: MenuItem) {
        this._prevElement = element
    }

    /**
     * Element setters: next
     * @param {MenuItem} element
     */
    public set nextElement(element: MenuItem) {
        this._nextElement = element
    }

    /**
     * Element setters for keyboard actions
     * @param {MenuItem} element
     * @param {ArrowDirection} direction
     */
    public setArrowElement(element: MenuItem, direction: ArrowDirection): void {
        this._arrowElement[direction] = element
    }

    /**
     * Element setters: next root item chain for removing attributes
     * @param {MenuItem} element
     */
    public set nextRootItem(element: MenuItem) {
        this._nextRootItem = element
    }

    /**
     * Remove styles and execute same method of the next root item
     */
    public removeRootHTMLClasses(): void {
        this.element.classList.remove(
            menuClass.active,
            mainMenuClass.noColumn,
            mainMenuClass.oneColumn,
            mainMenuClass.twoColumn
        )
        if (this.childrenWrapper && !isMobile()) {
            setStyles(this.childrenWrapper, { display: 'none' })
        }

        if (this._nextRootItem) {
            this._nextRootItem.removeRootHTMLClasses()
        }
    }

    /**
     * Handle when keyboard is used on the menu items
     * Escape: should close the menu
     * Tab and shift tab: navigate to next and prev element
     * Arrow keys should let you move through the menu and sub menu items faster
     *
     * @param {KeyboardEvent} e
     */
    private handleKeyEvents(e: KeyboardEvent): void {
        const navigationEvents = [
            KeyType.DOWN,
            KeyType.DOWN_IE,
            KeyType.UP,
            KeyType.UP_IE,
            KeyType.RIGHT,
            KeyType.RIGHT_IE,
            KeyType.LEFT,
            KeyType.LEFT_IE,
            KeyType.TAB
        ] as string[]

        if (e.key === KeyType.ESC || e.key === KeyType.ESC_IE) {
            this.targetMenu.closeMenu()
            return
        }

        if (isMobile() || !navigationEvents.includes(e.key)) {
            return
        }

        switch (e.key) {
            case KeyType.DOWN:
            case KeyType.DOWN_IE:
            case KeyType.UP:
            case KeyType.UP_IE:
            case KeyType.RIGHT:
            case KeyType.RIGHT_IE:
            case KeyType.LEFT:
            case KeyType.LEFT_IE:
                // Arrow keys
                if (this._arrowElement[ArrowDirection[e.key]]) {
                    e.preventDefault()
                    this._arrowElement[ArrowDirection[e.key]].focus()
                }
                return
            case KeyType.TAB:
                if (!e.shiftKey && this._nextElement) {
                    // Tab: next element
                    e.preventDefault()
                    this._nextElement.focus()
                } else if (e.shiftKey && this._prevElement) {
                    // Shift + Tab: prev element
                    if (this._prevElement) {
                        e.preventDefault()
                        this._prevElement.focus()
                    }
                } else if (!e.shiftKey && !this._nextElement) {
                    // Tab on the last element: back to hamburger
                    e.preventDefault()
                    this.targetMenu.closeMenu()
                    this.targetMenu.controlBtn.focus()
                } else if (e.shiftKey && !this._prevElement) {
                    // Shift + Tab on the first element: back to hamburger
                    e.preventDefault()
                    this.targetMenu.closeMenu()
                    this.targetMenu.controlBtn.focus()
                }
                return
            default:
                return
        }
    }

    /**
     * Focus action
     */
    public focus(): void {
        this.targetMenu.removeRootItemClasses()

        if (this._arrowElement[ArrowDirection.Left] && !isMobile()) {
            this._arrowElement[ArrowDirection.Left].addActiveClass()
            if (this._arrowElement[ArrowDirection.Left].childrenWrapper) {
                setStyles(
                    this._arrowElement[ArrowDirection.Left].childrenWrapper,
                    {}
                )
            }
            this._arrowElement[ArrowDirection.Left].setLevel2Width()
        }

        this.focusItem.focus()
    }

    public addActiveClass(): void {
        this.element.classList.add(menuClass.active)
    }

    /**
     * Adjust the width of L2 and menu depending on number of columns
     * only applies to sm breakpoint up design
     */
    public setLevel2Width(): void {
        if (isMobile() || !this.targetMenu) {
            return
        }
        if (!this.children) {
            // No L2 items
            this.targetMenu.setMenuClass(mainMenuClass.noColumn)
            return
        }

        const firstElement = this.children[0]
        const lastElement = this.children[this.children.length - 1]

        if (firstElement && lastElement && this.childrenWrapper) {
            const firstPosX = firstElement.getClientRects()[0].x
            const lastPosX = lastElement.getClientRects()[0].x
            if (firstPosX !== lastPosX) {
                // if first element x position is different than last element x position
                // => we have 2 columns layout
                this.targetMenu.setMenuClass(mainMenuClass.twoColumn)
                this.childrenWrapper.classList.add(mainMenuClass.twoColumn)
                return
            }

            // if first element x position is same with last element x position
            // => we have 1 columns layout
            this.targetMenu.setMenuClass(mainMenuClass.oneColumn)
            this.childrenWrapper.classList.add(mainMenuClass.oneColumn)
            return
        }

        // No L2 items here
        this.targetMenu.setMenuClass(mainMenuClass.noColumn)
    }

    /**
     * Trace the last touched item
     * On Tablet or touchable desktop, the first touch should open the sub-menu
     *
     * @param {TouchEvent} e
     */
    private handleTouch(e: TouchEvent): void {
        // This is the second touch, so open the anchor
        if (this.targetMenu.lastTouchedL1Item === this) {
            return
        }

        // This is the first touch
        e.preventDefault()
        this.targetMenu.lastTouchedL1Item = this
        this.focus()
    }

    /**
     * Handle the dropDown menu on mobile
     * it opens or closes thee menu depending on thee statee of the list
     *
     * @param {TouchEvent} e
     */
    private handleTouchDroppableMobile(e: UIEvent): void {
        const styles = getStyles(this.childrenWrapper)
        styles['display'] === 'flex'
            ? this.closeDropDownL2Mobile()
            : this.openDropDownL2Mobile()

        e.preventDefault()
        this.targetMenu.lastTouchedL1Item = this
        this.focus()
    }

    /**
     * Opens dropdown and adjusts the arrow direction
     */
    private openDropDownL2Mobile(): void {
        this.childrenWrapperArrow?.setAttribute(
            'aria-label',
            'Click to close sub menu'
        )
        setStyles(this.childrenWrapper, { display: 'flex' })
        setStyles(this.childrenWrapperArrow, { transform: 'rotate(180deg)' })
    }

    /**
     * Closes dropdown and adjusts the arrow direction
     */
    private closeDropDownL2Mobile(): void {
        this.childrenWrapperArrow?.setAttribute(
            'aria-label',
            'Click to open sub menu'
        )
        setStyles(this.childrenWrapper, { display: 'none' })
        setStyles(this.childrenWrapperArrow, { transform: 'rotate(0deg)' })
    }

    /**
     * when the link is for ReactApp Pages
     * close the menu as if there were a regular link route event
     *
     * @param {String} href
     */
    private handleRouting(href: string): void {
        // is the href attribute not null
        // Does the href value contain a react route /#/
        // if not return
        if (!href.includes('/#/')) {
            return
        }
        // Close the menu when react route is clicked
        this.targetMenu.closeMenu()
    }
}
